import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Target, Settings } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useReadingGoals, type ReadingGoal } from '@/hooks/useReadingGoals';
import { supabase } from '@/integrations/supabase/client';
import { ReadingProgressBookshelf } from './ReadingProgressBookshelf';
import { ManageReadingGoalDialog } from './ManageReadingGoalDialog';

interface ReadingGoalDashboardWidgetProps {
  onClick?: () => void;
  onGoalComplete?: (booksRead: number) => void;
}

export function ReadingGoalDashboardWidget({ onClick, onGoalComplete }: ReadingGoalDashboardWidgetProps) {
  const { user } = useAuth();
  const { getGoalForYear } = useReadingGoals();
  const [goal, setGoal] = useState<ReadingGoal | null>(null);
  const [booksRead, setBooksRead] = useState(0);
  const [loading, setLoading] = useState(true);
  const [goalJustCompleted, setGoalJustCompleted] = useState(false);

  const currentYear = new Date().getFullYear();

  const loadGoalAndProgress = async () => {
    if (!user) return;

    try {
      setLoading(true);
      
      // Load reading goal for current year
      const goalData = await getGoalForYear(currentYear);
      setGoal(goalData);

      // Load books read this year (check both read_at and finished_at)
      const { data: allReadBooks } = await supabase
        .from('user_books')
        .select('read_at, finished_at')
        .eq('user_id', user.id)
        .eq('shelf', 'read');

      // Only count books with finished_at for yearly statistics
      const booksThisYear = allReadBooks?.filter(book => {
        const finishedDate = book.finished_at ? new Date(book.finished_at) : null;
        return finishedDate && finishedDate.getFullYear() === currentYear;
      }) || [];

      setBooksRead(booksThisYear.length);
      
      // Check if goal just completed
      if (goal && booksThisYear.length >= goal.target_books && !goalJustCompleted) {
        setGoalJustCompleted(true);
        if (onGoalComplete) {
          onGoalComplete(booksThisYear.length);
        }
      }
    } catch (error) {
      console.error('Error loading reading goal:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGoalAndProgress();
  }, [user]);

  if (loading) {
    return (
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5 text-primary" />
            {currentYear} Reading Goal
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-32 bg-muted animate-pulse rounded" />
        </CardContent>
      </Card>
    );
  }

  if (!goal) {
    return (
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5 text-primary" />
            Set Your {currentYear} Reading Goal
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Challenge yourself with a reading goal for {currentYear}! Track your progress throughout the year.
          </p>
          <ManageReadingGoalDialog 
            year={currentYear} 
            onGoalUpdated={loadGoalAndProgress}
            trigger={
              <Button variant="pill" className="w-full">
                <Target className="w-4 h-4 mr-2" />
                Set Reading Goal
              </Button>
            }
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <ReadingProgressBookshelf
      booksRead={booksRead}
      targetBooks={goal.target_books}
      year={currentYear}
      onClick={onClick}
      footerContent={
        <div className="flex justify-end pt-2">
          <ManageReadingGoalDialog 
            year={currentYear} 
            currentGoal={goal.target_books}
            onGoalUpdated={loadGoalAndProgress}
            trigger={
              <Button 
                variant="ghost" 
                size="sm"
                onClick={(e) => e.stopPropagation()}
              >
                <Settings className="w-4 h-4 mr-2" />
                Adjust Goal
              </Button>
            }
          />
        </div>
      }
    />
  );
}
